import Pill from '@pelckmans/elementis/components/pill';
import Tooltip from '@pelckmans/elementis/components/tooltip';
import { useTranslation } from 'react-i18next';
import IdentityContext from '../contexts/IdentityContext';
import { useContext } from 'react';
import { Module } from '../api/module';

const BASE = 'PAGES.CATALOGUE.MODULE.STATUS';

function ModuleStatus({
  access,
  module,
  demoModules,
  showUnlicensedStatus = false,
}: {
  access: boolean;
  module: Module;
  demoModules?: string[];
  showUnlicensedStatus?: boolean;
}) {
  const { identity } = useContext(IdentityContext);
  const { t } = useTranslation();
  const { id, publishingState, incomplete, licenses } = module;

  const isAuthor = identity.author_modules.includes(module.id);
  const isDemo = demoModules?.includes(id);
  const isUnlicensed =
    showUnlicensedStatus &&
    (module.publishingState === 'published' || module.publishingState === 'phasedOut') &&
    !licenses.isActiveBook;
  const role = t(`ROLES.${isAuthor ? 'AUTHOR' : identity.highestRole.toUpperCase()}`);

  return (
    <>
      {publishingState === 'preview' && (
        <Tooltip
          key={`preview-${id}`}
          truncation="wrapped"
          anchor={
            <Pill tooltip prominence="info" role="status">
              {t(`${BASE}.PREVIEW.LABEL`)}
            </Pill>
          }
        >
          {t(`${BASE}.PREVIEW.TOOLTIP.${access ? 'ACCESS' : 'NO_ACCESS'}`, { role })}
        </Tooltip>
      )}
      {publishingState === 'review' && (
        <Tooltip
          key={`review-${id}`}
          truncation="wrapped"
          anchor={
            <Pill tooltip prominence="info" role="status">
              {t(`${BASE}.REVIEW.LABEL`)}
            </Pill>
          }
        >
          {t(`${BASE}.REVIEW.TOOLTIP.VALUE`, { role })}
        </Tooltip>
      )}
      {publishingState === 'phasedOut' && (
        <Tooltip
          key={`phasedOut-${id}`}
          truncation="wrapped"
          anchor={
            <Pill tooltip prominence="warning" role="status">
              {t(`${BASE}.PHASED_OUT.LABEL`)}
            </Pill>
          }
        >
          {t(`${BASE}.PHASED_OUT.TOOLTIP.VALUE`, { role })}
        </Tooltip>
      )}
      {isUnlicensed && (
        <Tooltip
          key={`unlicensed-${id}`}
          truncation="wrapped"
          anchor={
            <Pill tooltip prominence="danger" role="status">
              {t(`${BASE}.UNLICENSED.LABEL`)}
            </Pill>
          }
        >
          {t(`${BASE}.UNLICENSED.TOOLTIP.VALUE`)}
        </Tooltip>
      )}
      {incomplete && (
        <Tooltip
          key={`incomplete-${id}`}
          truncation="wrapped"
          anchor={
            <Pill tooltip prominence="warning" role="status">
              {t(`${BASE}.INCOMPLETE.LABEL`)}
            </Pill>
          }
        >
          {t(`${BASE}.INCOMPLETE.TOOLTIP.VALUE`)}
        </Tooltip>
      )}
      {isDemo && (
        <Tooltip
          key={`demo-${id}`}
          truncation="wrapped"
          anchor={
            <Pill tooltip prominence="danger" role="status">
              {t(`${BASE}.DEMO.LABEL`)}
            </Pill>
          }
        >
          {t(`${BASE}.DEMO.TOOLTIP.VALUE`)}
        </Tooltip>
      )}
    </>
  );
}

export default ModuleStatus;
